/**
 * DriverTrackingPanel — staff-side controls for real-time pickup tracking.
 * Lets supervisors / crew leaders / agents:
 *   - pick a route assigned to them
 *   - start a session
 *   - auto-ping GPS every 30s while active (uses browser geolocation watchPosition)
 *   - mark stops arrived / completed
 *   - stop session
 */
import { useEffect, useRef, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Truck, Play, Square, MapPin, CheckCircle2, Navigation } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DriverTrackingPanel() {
  const [routes, setRoutes] = useState([]);
  const [selectedRouteId, setSelectedRouteId] = useState('');
  const [session, setSession] = useState(null);
  const [pinging, setPinging] = useState(false);
  const [lastPingAt, setLastPingAt] = useState(null);
  const [pingCount, setPingCount] = useState(0);
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);
  const lastFixRef = useRef(null);

  const loadRoutes = async () => {
    try {
      const res = await fetch(`${API}/api/routes`, { credentials: 'include' });
      if (res.ok) setRoutes(await res.json());
    } catch (_) { /* noop */ }
  };

  const loadCurrent = async () => {
    try {
      const res = await fetch(`${API}/api/tracking/my-current`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data && data.session_id) {
          // Resume UX: surface a toast so the driver knows tracking is live again
          const ageMin = data.session_age_seconds != null ? Math.round(data.session_age_seconds / 60) : null;
          if (data.needs_resume_confirmation) {
            // Stale-looking session (>8h) — confirm before re-attaching GPS watcher
            const proceed = window.confirm(
              `You have a tracking session that's been active for ~${ageMin || '?'} min. Resume it now? Cancel will end it.`
            );
            if (!proceed) {
              await fetch(`${API}/api/tracking/session/${data.session_id}/stop`, {
                method: 'POST', credentials: 'include',
              });
              return;
            }
          } else if (ageMin != null) {
            toast.success(`Welcome back — your tracking session resumed (active ${ageMin} min).`);
          }
          setSession(data);
          setSelectedRouteId(data.route_id);
          startWatch(data.session_id);
        }
      }
    } catch (_) { /* noop */ }
  };

  useEffect(() => {
    loadRoutes();
    loadCurrent();
    return () => {
      if (watchIdRef.current && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
     
  }, []);

  const refreshSession = async (sid) => {
    const id = sid || session?.session_id;
    if (!id) return;
    try {
      const res = await fetch(`${API}/api/tracking/session/${id}`, { credentials: 'include' });
      if (res.ok) setSession(await res.json());
    } catch (_) { /* noop */ }
  };

  const sendPing = async (sid, pos) => {
    try {
      setPinging(true);
      const res = await fetch(`${API}/api/tracking/session/${sid}/ping`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          speed: pos.coords.speed ?? null,
          heading: pos.coords.heading ?? null,
        }),
      });
      if (res.ok) {
        setLastPingAt(new Date());
        setPingCount((c) => c + 1);
      }
    } catch (_) { /* noop */ }
    finally { setPinging(false); }
  };

  const startWatch = (sid) => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported on this device.');
      return;
    }
    // High-accuracy continuous tracking
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => { lastFixRef.current = pos; },
      (err) => { console.warn('geo error', err); },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
    // Push the latest fix to backend every 30s
    intervalRef.current = setInterval(() => {
      if (lastFixRef.current) {
        sendPing(sid, lastFixRef.current);
        refreshSession(sid);
      }
    }, 30000);
  };

  const startSession = async () => {
    if (!selectedRouteId) return toast.error('Pick a route first');
    if (!navigator.geolocation) return toast.error('Geolocation unsupported');

    // Try to grab an initial fix; we won't block if the user denies
    const initial = await new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 8000 }
      );
    });

    try {
      const res = await fetch(`${API}/api/tracking/session/start`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          route_id: selectedRouteId,
          initial_lat: initial?.lat ?? null,
          initial_lng: initial?.lng ?? null,
        }),
      });
      const data = await res.json();
      if (!res.ok) return toast.error(data.detail || 'Failed to start');
      setSession(data);
      setPingCount(0);
      toast.success('Live tracking started — your team can now see your route.');
      startWatch(data.session_id);
    } catch (_) { toast.error('Network error'); }
  };

  const stopSession = async () => {
    if (!session) return;
    try {
      const res = await fetch(`${API}/api/tracking/session/${session.session_id}/stop`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) {
        toast.success('Session ended');
        setSession(null);
        if (watchIdRef.current && navigator.geolocation) {
          navigator.geolocation.clearWatch(watchIdRef.current);
          watchIdRef.current = null;
        }
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    } catch (_) { toast.error('Network error'); }
  };

  const markVisit = async (propertyId, status) => {
    if (!session) return;
    try {
      const res = await fetch(`${API}/api/tracking/session/${session.session_id}/visit/${propertyId}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, notes: '' }),
      });
      if (res.ok) {
        toast.success(status === 'completed' ? 'Stop completed' : 'Marked as arrived');
        refreshSession();
      }
    } catch (_) { toast.error('Network error'); }
  };

  const activeRoutes = routes.filter((r) => r.status !== 'completed');
  const stops = session?.stops || [];

  return (
    <Card className="bg-zinc-900 border-2 border-emerald-500/40 p-4" data-testid="driver-tracking-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="text-lg font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          <Navigation className="h-5 w-5 text-emerald-400" /> LIVE PICKUP TRACKING
        </h3>
        {session ? (
          <Badge className="bg-emerald-600 text-white animate-pulse" data-testid="tracking-live-badge">LIVE</Badge>
        ) : (
          <Badge className="bg-zinc-700 text-zinc-300">Inactive</Badge>
        )}
      </div>

      {!session ? (
        <div className="space-y-3">
          <p className="text-xs text-zinc-400">
            Start a tracking session for your assigned route — admin and Property Managers will see your live position and progress.
          </p>
          <div className="flex flex-wrap gap-2">
            <Select value={selectedRouteId} onValueChange={setSelectedRouteId}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white w-72" data-testid="tracking-route-select">
                <SelectValue placeholder="Select a route…" />
              </SelectTrigger>
              <SelectContent>
                {activeRoutes.length === 0 ? (
                  <SelectItem value="none" disabled>No routes assigned</SelectItem>
                ) : activeRoutes.map((r) => (
                  <SelectItem key={r.route_id} value={r.route_id}>
                    {r.name} · {r.property_ids?.length || 0} stops
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={startSession}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
              disabled={!selectedRouteId || activeRoutes.length === 0}
              data-testid="tracking-start-btn"
            >
              <Play className="h-4 w-4 mr-1" /> Start Tracking
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Status strip */}
          <div className="flex items-center justify-between flex-wrap gap-2 bg-zinc-800 border border-zinc-700 rounded-lg p-3" data-testid="tracking-status-strip">
            <div className="text-xs text-zinc-300">
              <div className="font-bold text-white flex items-center gap-2">
                {session.route_name}
                {session.session_age_seconds != null && session.session_age_seconds > 300 && (
                  <Badge className="bg-blue-600 text-white text-[10px]" data-testid="tracking-resumed-badge">RESUMED</Badge>
                )}
              </div>
              <div>{stops.length} stops · started {new Date(session.started_at).toLocaleTimeString()}</div>
            </div>
            <div className="text-[11px] text-zinc-400">
              <div>Pings sent: <span className="text-emerald-400 font-bold">{pingCount}</span></div>
              <div>Last ping: {lastPingAt ? lastPingAt.toLocaleTimeString() : '—'}</div>
            </div>
            <Button onClick={stopSession} variant="outline" className="border-red-500 text-red-400" data-testid="tracking-stop-btn">
              <Square className="h-4 w-4 mr-1" /> End session
            </Button>
          </div>

          {/* Stops list with arrived/completed actions */}
          <ol className="space-y-2" data-testid="tracking-stops-list">
            {stops.map((s, i) => (
              <li key={s.property_id} className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 flex items-center gap-3" data-testid={`tracking-stop-${s.property_id}`}>
                <span className="text-zinc-400 text-xs w-5">{i + 1}.</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{s.property_name}</div>
                  <div className="text-[11px] text-zinc-400 truncate">{s.address || '—'}</div>
                </div>
                {s.status === 'pending' && (
                  <Button size="sm" onClick={() => markVisit(s.property_id, 'arrived')}
                    className="bg-[#FFD60A] text-black hover:bg-[#EAB308]" data-testid={`tracking-arrived-${s.property_id}`}>
                    <MapPin className="h-3 w-3 mr-1" /> Arrived
                  </Button>
                )}
                {s.status === 'arrived' && (
                  <Button size="sm" onClick={() => markVisit(s.property_id, 'completed')}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`tracking-complete-${s.property_id}`}>
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Complete
                  </Button>
                )}
                {s.status === 'completed' && (
                  <Badge className="bg-emerald-600 text-white"><CheckCircle2 className="h-3 w-3 mr-1" />Done</Badge>
                )}
              </li>
            ))}
          </ol>

          {pinging && <div className="text-[10px] text-emerald-400">Sending GPS…</div>}
          <p className="text-[10px] text-zinc-500">
            <Truck className="h-3 w-3 inline mr-1" /> Tip: keep this tab open while driving — GPS pings every 30 seconds and powers the live map for your team.
          </p>
        </div>
      )}
    </Card>
  );
}
